import { Node, Edge } from 'reactflow';
import { cleanWorkflowSchema, CleanWorkflowSchema } from '../utils/workflow-cleaner';
import { WorkflowBaseService } from '../services/WorkflowBaseService';
import { AgentBaseService } from '@/features/Agents/services/agentBaseService';
import { post, engineApiBase, engineEventsUrl } from '@/shared/utils/fetchApi';
import { SSEConnection } from '@/shared/utils/sseConnection';

/**
 * Interface para os callbacks de eventos do teste de workflow
 */
export interface WorkflowTestCallbacks {
  onNodeStatusChange: (nodeId: string, status: 'running' | 'success' | 'error' | 'waiting', result?: Record<string, unknown> | null) => void;
  onComplete: (status: 'success' | 'error' | 'stopped', results?: Record<string, unknown>) => void;
  onTriggerWaiting: (triggerType: 'webhook' | 'storage', nodeId: string, config: Record<string, unknown>) => void;
  onWebhookReceived?: (nodeId: string, data: Record<string, unknown>) => void;
  onWebhookProcessed?: (nodeId: string, data: Record<string, unknown>) => void;
  onWorkflowStatusChange?: (status: 'paused' | 'running' | 'stopped', message?: string) => void;
  onWorkflowCompleted?: (data?: Record<string, unknown>) => void; // Novo callback para conclusão do workflow
  onScheduleRegistered?: (nodeId: string, data: Record<string, unknown>) => void; // Callback para schedule registrado
  onScheduleTriggered?: (nodeId: string, data: Record<string, unknown>) => void; // Callback para schedule trigger
}

/**
 * Classe responsável pelo gerenciamento de teste de workflow
 */
export class WorkflowTest {
  private callbacks: WorkflowTestCallbacks;
  private abortController: AbortController | null = null;
  private executionId: string | null = null;
  private eventSource: EventSource | null = null;
  private activeWebhooks: Map<string, string> = new Map();
  
  private registeredWebhooks: Record<string, unknown>[] = []; // Adicionado para armazenar webhooks registrados
  private triggerIdToNodeIdMap: Map<string, string> = new Map(); // Mapeia triggerId para nodeId
  private triggeredNodes: Map<string, boolean> = new Map(); // Mapa de nós triggered
  private workflowData: Record<string, unknown> | null = null; // Armazenar os dados do workflow
  
  constructor(
    callbacks: WorkflowTestCallbacks
  ) {
    this.callbacks = callbacks;
  }
  
  /**
   * Prepara o schema do workflow para envio ao servidor
   */
  private async prepareSchema(
    nodes: Node[], 
    edges: Edge[], 
    options?: { organizationId?: string; workspaceId?: string }
  ): Promise<CleanWorkflowSchema> {
    // Utiliza a função de limpeza do schema do workflow-cleaner.ts
    return await cleanWorkflowSchema(nodes, edges, options);
  }
  
  /**
   * Inicia o teste do workflow
   */
  async startTest(nodes: Node[], edges: Edge[], organizationId: string, workspaceId: string): Promise<string> {
    try {
      // Limpar qualquer teste anterior
      this.stopTest();
      
      // Se ainda houver uma execução ativa (verificação adicional)
      if (this.executionId) {
        console.log(`Encerrando execução anterior: ${this.executionId}`);
        // Limpar apenas as referências internas sem iniciar nova chamada ao servidor
        this.clearExecution();
      }
      
      // Antes de preparar o schema para o teste, vamos tentar obter os prompts do agente relacionado
      // Primeiro, identificamos o ID do workflow a partir dos dados dos nós
      let workflowId = '';
      for (const node of nodes) {
        if (node.data?.workflowId) {
          workflowId = node.data.workflowId;
          break;
        }
      }
      
      console.log(`[DEBUG PROMPTS] WorkflowId obtido: ${workflowId}`);
      
      // Obtém os prompts do agente associado ao workflow
      let agentPrompts: Record<string, unknown>[] = [];
      if (workflowId) {
        try {
          console.log(`[DEBUG PROMPTS] Buscando agent_base_id para o workflow ${workflowId}`);
          
          // Consultar o agentId diretamente da tabela workflow_base
          const workflowData = await WorkflowBaseService.getById(workflowId);
          
          console.log(`[DEBUG PROMPTS] Dados do workflow:`, workflowData);
          
          const agentId = workflowData?.agent_base_id;
          
          if (agentId) {
            console.log(`[DEBUG PROMPTS] Buscando prompts do agente ${agentId}`);
            
            // Buscar os prompts usando o AgentBaseService
            const { data: agentData, error: agentError } = await AgentBaseService.getAgentBase(agentId);
            
            if (agentError) {
              console.error(`[DEBUG PROMPTS] Erro ao buscar agente: ${agentError.message}`, agentError);
            } else {
              console.log(`[DEBUG PROMPTS] Dados do agente:`, agentData);
              
              // Extrair os prompts do campo 'settings' do agente
              const settings = agentData?.settings as Record<string, unknown> | undefined;
              if (settings && Array.isArray(settings.prompts)) {
                agentPrompts = settings.prompts;
                console.log(`[DEBUG PROMPTS] Prompts adicionados ao schema:`, agentPrompts);
              } else {
                console.warn(`[DEBUG PROMPTS] Agente não possui prompts definidos:`, agentData);
              }
            }
          } else {
            console.warn(`[DEBUG PROMPTS] Workflow sem agent_base_id definido:`, workflowData);
          }
        } catch (error) {
          console.error(`[DEBUG PROMPTS] Erro ao tentar obter prompts do agente:`, error);
          // Não interromper a execução se não conseguir obter os prompts
        }
      }
      
      // Preparar o schema para o teste (limpa e formata a estrutura)
      const schema = await this.prepareSchema(nodes, edges, { organizationId, workspaceId });
      
      // Adicionar os prompts obtidos do agente ao schema (se existirem)
      if (agentPrompts.length > 0) {
        schema.prompts = agentPrompts;
        console.log('[DEBUG PROMPTS] Prompts do agente adicionados ao schema de teste');
      } else {
        console.warn('[DEBUG PROMPTS] Nenhum prompt foi adicionado ao schema');
      }
      
      // Parâmetros são incluídos automaticamente no prepareSchema via workflow-cleaner
      console.log('[WorkflowTest] Parâmetros do agente foram incluídos automaticamente no schema');
      if (schema.parameters && Object.keys(schema.parameters).length > 0) {
        console.log('[WorkflowTest] Parâmetros encontrados no schema:', schema.parameters);
      }
      
      // Criar o payload que será enviado para o endpoint
      const payloadJSON = JSON.stringify(schema);
      
      // Log do payload FINAL de teste (exatamente o que será enviado ao servidor)
      console.log('===== PAYLOAD FINAL DO TESTE DE WORKFLOW =====');
      console.log('Este é o payload exato enviado para o endpoint após preparação:');
      console.log(payloadJSON);
      console.log('');
      console.log('Versão formatada:');
      console.log(JSON.stringify(schema, null, 2));
      console.log('===============================================');
      
      // Configurar o AbortController para o fetch
      this.abortController = new AbortController();
      
      // Enviar para o endpoint de teste usando o utility post
      try {
        const result = await post<{ executionId?: string }>(`${engineApiBase()}/workflow/execute`, JSON.parse(payloadJSON), {
          signal: this.abortController.signal
        });

        this.executionId = result.executionId ?? null;
        
        if (!this.executionId) {
          throw new Error('ID de execução não retornado pelo servidor');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
        throw new Error(`Erro ao iniciar teste de workflow: ${errorMessage}`);
      }
      
      // Iniciar o monitoramento de eventos
      this.monitorEvents(this.executionId);
      
      return this.executionId;
    } catch (error) {
      console.error('Erro ao iniciar teste do workflow:', error);
      throw error;
    }
  }
  
  /**
   * Monitora os eventos de execução do workflow
   */
  monitorEvents(executionId: string): void {
    // Fechar conexão anterior se existir
    if (this.eventSource) {
      this.eventSource.close();
    }
    
    // Criar nova conexão SSE
    const connection = new SSEConnection({
      url: engineEventsUrl(executionId),
      maxRetries: 10,
      initialRetryDelay: 1000,
      maxRetryDelay: 16000
    });

    const eventSource = connection.connect();
    this.eventSource = eventSource;
    
    // Configurar handlers para eventos
    this.eventSource.onopen = () => {
      console.log('Conexão SSE estabelecida para o teste de workflow');
    };
    
    this.eventSource.onerror = (error) => {
      console.error('Erro na conexão SSE:', error);
      // Em caso de erro, tentamos reconectar ou notificamos falha
      if (this.executionId) {
        console.log('Tentando reconectar ao SSE...');
        setTimeout(() => {
          if (this.executionId) {
            console.log('Reconectando ao SSE para:', this.executionId);
            this.monitorEvents(this.executionId);
          }
        }, 2000);
      } else {
        this.callbacks.onComplete('error', { error: 'Erro na conexão com o servidor' });
        this.stopTest();
      }
    };
    
    // Configurar todos os listeners de eventos específicos
    this.setupEventListeners();
    
    // Handler genérico para eventos - Manter esse handler para eventos não específicos
    this.eventSource.onmessage = (event) => {
      try {
        console.log('Evento SSE recebido (onmessage):', event);
        console.log('Dados do evento:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        console.log('Dados parseados:', data);
        
        // Verificar se é um evento webhook-registered não capturado pelo listener específico
        if (data.type === 'webhook-registered' || data.event === 'webhook-registered') {
          console.log('🚨 Evento webhook-registered detectado no handler genérico:', data);
          this.handleWebhookRegistered(data);
        } else {
          // Processar outros tipos de eventos
          this.handleEvent(data);
        }
      } catch (error) {
        console.error('Erro ao processar evento:', error, event.data);
      }
    };
    
    // Adicionar listener específico para o evento "message"
    this.eventSource.addEventListener('message', (event) => {
      try {
        console.log('Evento "message" recebido:', event);
        console.log('Dados do evento message:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        console.log('Dados message parseados:', data);
        
        // Priorizar o uso do campo 'id' para identificar o nó no workflow
        const nodeId = data.id || data.nodeId || data.triggerId;
        
        // Processar evento webhook-received (pode ser importante para fechar o diálogo)
        if (data.type === 'webhook-received' || data.status === 'webhook-received') {
          console.log('Evento webhook-received detectado:', data);
          if (nodeId) {
            // Notificar que o webhook foi recebido para o nó apropriado
            this.callbacks.onNodeStatusChange(nodeId, 'running');
          } else {
            console.error('Evento webhook-received sem id válido:', data);
          }
        }
        
        // Verificar se é um evento de webhook
        if (data.type === 'webhook-registered' || 
            data.event === 'webhook-registered' || 
            (data.status && data.status === 'waiting-trigger') ||
            data.webhookPath) {
          console.log('Webhook detectado no evento message:', data);
          if (nodeId) {
            this.callbacks.onTriggerWaiting('webhook', nodeId, {
              webhookUrl: data.webhookPath || data.path,
              fullUrl: data.fullUrl, // Garantir que o fullUrl seja encaminhado ao componente
              ...data
            });
            this.callbacks.onNodeStatusChange(nodeId, 'waiting');
          } else {
            console.error('Evento de webhook sem id válido:', data);
          }
        }
      } catch (error) {
        console.error('Erro ao processar evento message:', error, event.data);
      }
    });
    
    // Adicionar um listener para qualquer evento sem tipo definido
    this.eventSource.addEventListener('', (event) => {
      try {
        console.log('Evento sem tipo recebido:', event);
        if (event.data) {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          console.log('Dados do evento sem tipo:', data);
          
          // Priorizar o uso do campo 'id' para identificar o nó
          const nodeId = data.id || data.nodeId || data.triggerId;
          const webhookUrl = data.webhookPath || data.path;
          
          // Verificar se pode ser um evento de webhook
          if (data.status === 'waiting-trigger' || data.webhookPath) {
            console.log('Possível webhook em evento sem tipo detectado:', data);
            
            if (nodeId && webhookUrl) {
              this.callbacks.onTriggerWaiting('webhook', nodeId, {
                webhookUrl,
                ...data
              });
              this.callbacks.onNodeStatusChange(nodeId, 'waiting');
            } else {
              console.error('Evento sem tipo relacionado a webhook sem id ou url válidos:', data);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao processar evento sem tipo:', error, event);
      }
    });
    
    // Adicionar listener específico para webhook-received
    this.eventSource.addEventListener('webhook-received', (event) => {
      try {
        console.log('Evento webhook-received específico:', event);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Usar o campo 'id' para identificar o nó no workflow
        const nodeId = data.id;
        
        if (nodeId) {
          console.log('Webhook foi recebido para o node:', nodeId);
          this.callbacks.onNodeStatusChange(nodeId, 'running');
          // Podemos usar um callback especial para fechar o diálogo se necessário
          if (this.callbacks.onWebhookReceived) {
            this.callbacks.onWebhookReceived(nodeId, data);
          }
        } else {
          console.error('Evento webhook-received sem id válido:', data);
        }
      } catch (error) {
        console.error('Erro ao processar evento webhook-received:', error);
      }
    });
    
    // Adicionar um handler para node-completed
    this.eventSource.addEventListener('node-completed', (event) => {
      try {
        console.log('Evento node-completed recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Usar o campo 'id' para identificar o nó no workflow
        let nodeId = data.id || data.nodeId;
        const nodeType = data.nodeType || data.type;
        const triggerId = data.triggerId;
        
        // Se não tiver nodeId direto, verificar se temos o triggerId mapeado
        if (!nodeId && triggerId && this.triggerIdToNodeIdMap.has(triggerId)) {
          nodeId = this.triggerIdToNodeIdMap.get(triggerId);
          console.log(`Encontrado nodeId (${nodeId}) pelo mapeamento de triggerId (${triggerId}) para node-completed`);
        }
        
        console.log(`Detalhes do evento node-completed: nodeId=${nodeId}, nodeType=${nodeType}, triggerId=${triggerId}`, data);
        
        if (nodeId) {
          // Log detalhado para monitoramento
          console.log(`Nó ${nodeId} (tipo: ${nodeType}) completou o processamento. Definindo status='success'`);
          
          // Aplicar a mesma técnica de atualização em duas fases para todos os tipos de nós
          console.log(`Atualizando status do nó ${nodeId} para 'success'`, data.result);
          setTimeout(() => {
            // Limpar quaisquer status anteriores antes de definir o novo
            if (this.callbacks.onNodeStatusChange) {
              // Primeiro resetar o status (usar 'waiting' em vez de string vazia)
              this.callbacks.onNodeStatusChange(nodeId, 'waiting', null);
              
              // Depois de um pequeno delay, definir o status final
              setTimeout(() => {
                console.log(`[FORCE] Definindo status final do nó ${nodeId} para 'success'`);
                this.callbacks.onNodeStatusChange(nodeId, 'success', data.result);
              }, 50);
            }
          }, 100);
          
          // Podemos usar um callback especial para fechar o diálogo se necessário
          if (this.callbacks.onWebhookProcessed) {
            this.callbacks.onWebhookProcessed(nodeId, data);
          }
        } else {
          console.error('Evento node-completed sem id válido:', data);
        }
      } catch (error) {
        console.error('Erro ao processar node-completed:', error);
      }
    });
    
    // Adicionar listener específico para webhook-triggered
    this.eventSource.addEventListener('webhook-triggered', (event) => {
      try {
        console.log('Evento webhook-triggered recebido:', event);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        console.log('Dados do evento webhook-triggered:', data);
        
        // Priorizar o uso do campo 'id' para identificar o nó no workflow
        // No evento webhook-triggered, o id é o ID do webhook, não o nodeId
        let nodeId = null;
        const webhookId = data.id;
        
        // Verificar se temos este webhookId mapeado para um nodeId
        if (webhookId && this.triggerIdToNodeIdMap.has(webhookId)) {
          nodeId = this.triggerIdToNodeIdMap.get(webhookId);
          console.log(`🔍 Encontrado nodeId (${nodeId}) pelo mapeamento de webhookId (${webhookId})`);
        }
        
        // Verificar pelo triggerId também
        const triggerId = data.triggerId || data.nodeId;
        if (!nodeId && triggerId) {
          nodeId = triggerId;
          console.log(`🔍 Usando triggerId/nodeId direto: ${nodeId}`);
        }
        
        // Se ainda não tiver id, tenta outros métodos de identificação
        if (!nodeId) {
          console.log('Evento webhook-triggered sem id mapeado, tentando identificar pelo path');
          // Buscar o nodeId do nó de webhook pelo path
          const webhookPath = data.path;
          
          if (webhookPath) {
            // Tentar obter pelo mapa de webhooks ativos (método mais confiável)
            nodeId = this.getNodeIdByWebhookPath(webhookPath);
            
            // Se não encontrou, tenta pelos webhooks registrados (fallback)
            if (!nodeId && this.registeredWebhooks.length > 0) {
              const webhook = this.registeredWebhooks.find(wh => 
                wh.path === webhookPath || 
                wh.path === `/${webhookPath}` || 
                `/${wh.path}` === webhookPath
              );
              
              if (webhook) {
                nodeId = webhook.nodeId;
                console.log(`Encontrado nó de webhook pelo path ${webhookPath} nos registrados: ${nodeId}`);
              }
            }
            
            // Se encontrou o nodeId pelo path, registra-o para futuras referências
            if (nodeId) {
              console.log(`Encontrado nó de webhook pelo path ${webhookPath}: ${nodeId}`);
            }
          }
        } else {
          console.log(`Nó identificado pelo mapeamento: ${nodeId}`);
        }
        
        if (nodeId) {
          console.log(`Webhook foi acionado para o nó: ${nodeId}`);
          
          // Atualizamos o status do nó para evitar deadlocks na execução
          this.markNodeAsTriggered(nodeId);
          
          // Atualizar o status do nó para running
          this.callbacks.onNodeStatusChange(nodeId, 'running');
          
          // Fechar qualquer diálogo de espera que possa estar aberto
          if (this.callbacks.onWebhookReceived) {
            // Garantir que o evento seja identificável como webhook-triggered
            data.type = data.type || 'webhook-triggered';
            data.event = data.event || 'webhook-triggered';
            this.callbacks.onWebhookReceived(nodeId, data);
          }
        } else {
          console.error('Não foi possível identificar o nó de webhook pelo path:', data.path);
        }
      } catch (error) {
        console.error('Erro ao processar evento webhook-triggered:', error);
      }
    });
    
    // Adicionar listener específico para webhook-registered
    this.eventSource.addEventListener('webhook-registered', (event) => {
      try {
        console.log('✅ Evento webhook-registered recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        console.log('📌 Dados do webhook-registered:', data);
        
        // Extrair informações do webhook
        // Armazenar tanto o id quanto o triggerId para mapeamento correto
        const nodeId = data.triggerId;
        const webhookId = data.id; // Guardar o ID do webhook para comparar depois
        const webhookUrl = data.fullUrl;
        
        if (nodeId) {
          console.log(`🔗 Webhook registrado para o nó ${nodeId}: ${webhookUrl}`);
          
          // Mapear o ID do webhook para o nodeId para referência futura
          if (webhookId) {
            this.triggerIdToNodeIdMap.set(webhookId, nodeId);
            console.log(`🔄 Mapeando webhookId (${webhookId}) para nodeId (${nodeId})`);
          }
          
          // Registrar o webhook ativo
          if (webhookUrl) {
            this.activeWebhooks.set(nodeId, webhookUrl);
            console.log(`ℹ️ Webhook ativo registrado: nodeId=${nodeId}, path=${webhookUrl}`);
          }
          
          // Notificar a UI sobre o webhook registrado
          this.callbacks.onTriggerWaiting('webhook', nodeId, {
            webhookUrl,
            fullUrl: data.fullUrl, // Garantir que o fullUrl seja encaminhado ao componente
            webhookId, // Incluir o ID do webhook para referência
            ...data
          });
          
          this.callbacks.onNodeStatusChange(nodeId, 'waiting');
        } else {
          console.error('❌ Evento webhook-registered sem nodeId/triggerId válido:', data);
        }
      } catch (error) {
        console.error('❌ Erro ao processar evento webhook-registered:', error);
      }
    });

    // Adicionar listener específico para eventos node-started
    this.eventSource.addEventListener('node-started', (event) => {
      try {
        console.log('Evento node-started recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Usar o campo 'id' para identificar o nó no workflow
        // Este id é do formato "node_42" ou similar
        const nodeId = data.id || data.nodeId;
        const nodeType = data.nodeType || data.type;
        
        console.log(`Detalhes do evento node-started: nodeId=${nodeId}, nodeType=${nodeType}`, data);
        
        if (nodeId) {
          console.log(`Nó ${nodeId} (tipo: ${nodeType}) iniciou o processamento`);
          
          // Log específico para nós de trigger
          if (nodeType === 'webhook_trigger' || nodeType?.includes('webhook') || data.triggerType === 'webhook') {
            console.log(`Nó de TRIGGER (webhook) iniciado: ${nodeId}`, data);
          }
          
          // Atualizar o status do nó para running (ativará o spinner)
          this.callbacks.onNodeStatusChange(nodeId, 'running');
        } else {
          console.error('Evento node-started sem id válido:', data);
        }
      } catch (error) {
        console.error('Erro ao processar evento node-started:', error, event.data);
      }
    });
    
    // Configurar handlers específicos
    this.setupEventHandlers();
  }
  
  /**
   * Configura os handlers para eventos específicos
   */
  private setupEventHandlers(): void {
    if (!this.eventSource) return;
    
    // Adicionar listener para o evento "node-started"
    this.eventSource.addEventListener('node-started', (event) => {
      try {
        console.log('Evento node-started recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Usar o campo 'id' para identificar o nó no workflow
        // Este id é do formato "node_42" ou similar
        const nodeId = data.id || data.nodeId;
        const nodeType = data.nodeType || data.type;
        
        console.log(`Detalhes do evento node-started: nodeId=${nodeId}, nodeType=${nodeType}`, data);
        
        if (nodeId) {
          console.log(`Nó ${nodeId} (tipo: ${nodeType}) iniciou o processamento`);
          
          // Log específico para nós de trigger
          if (nodeType === 'webhook_trigger' || nodeType?.includes('webhook') || data.triggerType === 'webhook') {
            console.log(`Nó de TRIGGER (webhook) iniciado: ${nodeId}`, data);
          }
          
          // Atualizar o status do nó para running (ativará o spinner)
          this.callbacks.onNodeStatusChange(nodeId, 'running');
        } else {
          console.error('Evento node-started sem id válido:', data);
        }
      } catch (error) {
        console.error('Erro ao processar evento node-started:', error, event.data);
      }
    });
    
    // Adicionar listener para o evento "status"
    this.eventSource.addEventListener('status', (event) => {
      try {
        console.log('Evento específico \'status\' recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Verificar se é um evento 'started' inicial do workflow (sem nodeId específico)
        if (data.status === 'started' && data.isTriggerNode && !data.nodeId && !data.triggerId) {
          console.log('Evento de início de workflow recebido. Nenhum nó específico para atualizar neste momento.');
          
          // Opcionalmente, podemos notificar sobre a mudança de status do workflow
          if (this.callbacks.onWorkflowStatusChange) {
            this.callbacks.onWorkflowStatusChange('running', data.message);
          }
          return;
        }
        
        // Processar eventos de status de nós
        if (data.nodeId) {
          this.callbacks.onNodeStatusChange(data.nodeId, data.status as "running" | "success" | "error" | "waiting", data.result);
        } else if (data.id) {
          // Caso o nodeId esteja no campo 'id'
          this.callbacks.onNodeStatusChange(data.id, data.status as "running" | "success" | "error" | "waiting", data.result);
        } else if (data.triggerId) {
          // Para eventos relacionados a triggers que só têm triggerId
          const nodeId = data.triggerId;
          // Verificar se temos este triggerId mapeado para um nodeId
          const mappedNodeId = this.triggerIdToNodeIdMap.get(data.triggerId);
          
          if (mappedNodeId) {
            console.log(`Usando nodeId mapeado ${mappedNodeId} para o triggerId ${nodeId}`);
            this.callbacks.onNodeStatusChange(mappedNodeId, data.status as "running" | "success" | "error" | "waiting", data.result);
          } else {
            console.log(`Usando triggerId ${nodeId} como nodeId pois não há mapeamento`);
            this.callbacks.onNodeStatusChange(nodeId, data.status as "running" | "success" | "error" | "waiting", data.result);
          }
        } else if (data.isTriggerNode && data.startNodeType) {
          console.log('Evento de trigger recebido sem ID específico, ignorando atualização de status');
        } else {
          console.log('Evento status sem nodeId/triggerId válido:', data);
        }
        
        // Detectar evento waiting-trigger para webhook
        if (data.status === 'waiting-trigger' && (data.webhookPath || data.path)) {
          console.log('Evento status com waiting-trigger detectado:', data);
          const nodeId = data.id;
          const triggerId = data.triggerId;
          const webhookUrl = data.webhookPath || data.path;
          
          if (nodeId) {
            console.log(`Nó ${nodeId} (triggerId: ${triggerId}) está esperando por trigger de webhook`);
            
            this.callbacks.onTriggerWaiting('webhook', nodeId, {
              webhookUrl,
              triggerId,
              ...data
            });
            
            // Registrar o mapeamento entre triggerId e nodeId para uso posterior
            if (triggerId) {
              this.triggerIdToNodeIdMap.set(triggerId, nodeId);
            }
            
            this.callbacks.onNodeStatusChange(nodeId, 'waiting');
          } else {
            console.error('Evento waiting-trigger sem nodeId válido:', data);
          }
        }
        
        // Lidar com eventos de conclusão
        if (data.status === 'completed' || data.status === 'error') {
          this.callbacks.onComplete(
            data.status === 'completed' ? 'success' : 'error',
            data
          );
        }
      } catch (error) {
        console.error('Erro ao processar evento de status:', error, event.data);
      }
    });
    
    // Lidar com eventos de conclusão
    // if (data.status === 'completed' || data.status === 'error') {
    //   this.callbacks.onComplete(
    //     data.status === 'completed' ? 'success' : 'error',
    //     data
    //   );
    // }
  }
  
  /**
   * Processa eventos genéricos
   */
  private handleEvent(data: Record<string, unknown>): void {
    console.log('Processando evento genérico:', data);

    try {
      // Verifica eventos de status de nó
      if (data.nodeId && data.status) {
        this.callbacks.onNodeStatusChange(data.nodeId as string, data.status as "running" | "success" | "error" | "waiting", data.result as Record<string, unknown> | null | undefined);
        return;
      }

      // Verifica evento de status com webhook
      if (data.status === 'waiting-trigger' && (data.webhookPath || data.path)) {
        console.log('Evento status com waiting-trigger detectado:', data);

        // Priorizar o uso do ID do nó no editor (node_XX) em vez do triggerId
        const nodeId = data.id as string | undefined;
        // Manter o triggerId para correspondência posterior se necessário
        const triggerId = data.triggerId as string | undefined;
        const webhookUrl = (data.webhookPath || data.path) as string | undefined;

        if (nodeId) {
          console.log(`Nó ${nodeId} (triggerId: ${triggerId}) está esperando por trigger de webhook`);

          this.callbacks.onTriggerWaiting('webhook', nodeId, {
            webhookUrl,
            triggerId,
            ...data
          });

          // Registrar o mapeamento entre triggerId e nodeId para uso posterior
          if (triggerId) {
            this.triggerIdToNodeIdMap.set(triggerId, nodeId);
          }

          this.callbacks.onNodeStatusChange(nodeId, 'waiting');
        } else {
          console.error('Evento waiting-trigger sem nodeId válido:', data);
        }
        return;
      }

      // Verifica evento de registro de webhook específico (webhook-registered)
      if ((data.TriggerId || data.triggerId) && (data.Path || data.path || data.webhookPath)) {
        console.log('Registro de webhook detectado:', data);
        const nodeId = (data.TriggerId || data.triggerId || data.nodeId) as string | undefined;
        const webhookUrl = data.Path || data.path || data.webhookPath;

        if (nodeId) {
          this.callbacks.onTriggerWaiting('webhook', nodeId, {
            webhookUrl,
            fullUrl: data.fullUrl, // Garantir que o fullUrl seja encaminhado ao componente
            ...data
          });
          this.callbacks.onNodeStatusChange(nodeId, 'waiting');
        }
        return;
      }

      // Verifica eventos de conclusão
      if (data.status === 'completed' || data.status === 'error') {
        this.callbacks.onComplete(
          data.status === 'completed' ? 'success' : 'error',
          data
        );
      }
    } catch (error) {
      console.error('Erro ao processar evento:', error);
    }
  }
  
  /**
   * Pausa o teste em execução
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async pauseTest(): Promise<boolean> {
    if (!this.executionId) {
      console.error('Nenhum teste em execução para pausar');
      return false;
    }
    
    try {
      console.log(`Pausando teste de workflow: ${this.executionId}`);
      // Usar o utility post para fazer a requisição
      const data = await post<{ success?: boolean; message?: string }>(`${engineApiBase()}/workflow/${this.executionId}/pause`, {});
      console.log('Resposta da pausa do workflow:', data);

      if (!data.success) {
        console.error('Erro ao pausar workflow:', data.message);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao pausar teste de workflow:', error);
      return false;
    }
  }
  
  /**
   * Retoma um teste que estava pausado
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async resumeTest(): Promise<boolean> {
    if (!this.executionId) {
      console.error('Nenhum teste em execução para retomar');
      return false;
    }
    
    try {
      console.log(`Retomando teste de workflow: ${this.executionId}`);
      // Usar o utility post para fazer a requisição
      const data = await post<{ success?: boolean; message?: string }>(`${engineApiBase()}/workflow/${this.executionId}/continue`, {});
      console.log('Resposta da retomada do workflow:', data);

      if (!data.success) {
        console.error('Erro ao retomar workflow:', data.message);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao retomar teste de workflow:', error);
      return false;
    }
  }
  
  /**
   * Para o teste em execução e limpa recursos
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async stopTest(): Promise<boolean> {
    console.log('Parando teste de workflow');
    
    // Se não há execução ativa, apenas limpa os recursos
    if (!this.executionId) {
      this.clearData();
      return true;
    }
    
    try {
      const executionId = this.executionId; // Preserva o ID antes de limpar
      
      // Envia requisição para parar o workflow no backend usando o utility post
      console.log(`Solicitando parada do workflow: ${executionId}`);
      
      // Limpa os recursos antes da requisição para evitar problemas de estado
      this.clearData();
      
      // Usar o utility post para fazer a requisição
      const data = await post<{ success?: boolean; message?: string }>(`${engineApiBase()}/workflow/${executionId}/stop`, {});
      console.log('Resposta da parada do workflow:', data);

      if (!data.success) {
        console.warn('Aviso ao parar workflow:', data.message);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao parar teste de workflow:', error);
      // Garante que os recursos sejam limpos mesmo em caso de erro
      this.clearData();
      return false;
    }
  }

  clearData(): void {
    // Fecha a conexão EventSource se existir
    if (this.eventSource) {
      console.log('Fechando conexão SSE no clearData');
      this.eventSource.close();
      this.eventSource = null;
    }
    this.executionId = null;
    this.registeredWebhooks = []; // Limpa os webhooks registrados
    this.activeWebhooks.clear(); // Limpa os webhooks ativos
    this.triggerIdToNodeIdMap.clear(); // Limpa o mapa de triggerId para nodeId
  }

  /**
   * Limpa as referências internas de uma execução sem fazer novas chamadas ao servidor
   * Usado para evitar duplicidade de execuções
   */
  private clearExecution(): void {
    console.log('Limpando referências internas de execução anterior');
    this.executionId = null;
    this.triggeredNodes.clear();
    this.workflowData = null;
    
    // Fechar EventSource se existir
    if (this.eventSource) {
      console.log('Fechando conexão EventSource existente');
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  // Método para registrar um webhook ativo
  public registerActiveWebhook(nodeId: string, webhookPath: string) {
    console.log(`Registrando webhook ativo: nodeId=${nodeId}, path=${webhookPath}`);
    this.activeWebhooks.set(nodeId, webhookPath);
  }

  // Método para limpar webhooks ativos
  public clearActiveWebhooks() {
    this.activeWebhooks.clear();
  }

  // Método para obter o nodeId pelo path do webhook
  public getNodeIdByWebhookPath(path: string): string | null {
    // Normalizar o path
    const normalizedPath = this.normalizeWebhookPath(path);
    
    // Primeiro, tentamos encontrar pela correspondência direta no mapa activeWebhooks
    if (this.activeWebhooks.has(normalizedPath)) {
      return this.activeWebhooks.get(normalizedPath) || null;
    }
    
    return null;
  }

  // Método global para marcar um nó como triggered (para evitar repetição de triggers)
  markNodeAsTriggered(nodeId: string): void {
    console.log(`Marcando nó ${nodeId} como triggered`);
    // Salvar o nodeId no Map de nós triggered
    this.triggeredNodes.set(nodeId, true);
    
    // Para depuração, também salvar em uma variável global
    if (typeof window !== 'undefined') {
      // @ts-expect-error - Adicionando propriedade para debug
      window.triggeredNodes = window.triggeredNodes || {};
      // @ts-expect-error - Adicionando propriedade para debug
      window.triggeredNodes[nodeId] = true;
    }
  }

  // Método para lidar com eventos webhook-registered não capturados pelo listener específico
  private handleWebhookRegistered(data: Record<string, unknown>) {
    console.log('Processando evento webhook-registered:', data);
    // Extrair informações do webhook
    const nodeId = (data.nodeId || data.triggerId) as string | undefined;
    const webhookUrl = (data.path || data.webhookPath || data.fullUrl) as string | undefined;

    if (nodeId) {
      console.log(`🔗 Webhook registrado para o nó ${nodeId}: ${webhookUrl}`);

      // Registrar o webhook ativo
      if (webhookUrl) {
        this.activeWebhooks.set(nodeId, webhookUrl);
        console.log(`ℹ️ Webhook ativo registrado: nodeId=${nodeId}, path=${webhookUrl}`);
      }

      // Notificar a UI sobre o webhook registrado
      this.callbacks.onTriggerWaiting('webhook', nodeId, {
        webhookUrl,
        fullUrl: data.fullUrl, // Garantir que o fullUrl seja encaminhado ao componente
        ...data
      });

      this.callbacks.onNodeStatusChange(nodeId, 'waiting');
    } else {
      console.error('❌ Evento webhook-registered sem nodeId/triggerId válido:', data);
    }
  }

  // Método para verificar se o caminho do webhook bate com algum nó do workflow
  // Os paths podem ser diferentes em formato (http://algo vs /algo), então fazemos uma comparação normalizada
  findNodeIdForWebhookPath(webhookPath: string, data: Record<string, unknown>): string | null {
    console.log(`Procurando nó para o caminho de webhook: ${webhookPath}`);
    
    // Se o caminho estiver vazio ou nulo, não podemos fazer a correspondência
    if (!webhookPath) {
      console.error('Caminho de webhook vazio ou nulo');
      return null;
    }
    
    // Normalizar o path
    const normalizedPath = this.normalizeWebhookPath(webhookPath);
    
    // Primeiro, tentamos encontrar pela correspondência direta no mapa activeWebhooks
    if (this.activeWebhooks.has(normalizedPath)) {
      return this.activeWebhooks.get(normalizedPath) || null;
    }
    
    // Se não encontrou pelo path, verifica se há um triggerId no evento que pode ser mapeado
    const triggerId = data.triggerId as string | undefined;
    if (triggerId && this.triggerIdToNodeIdMap.has(triggerId)) {
      const nodeId = this.triggerIdToNodeIdMap.get(triggerId) || null;
      console.log(`Encontrado nodeId (${nodeId}) pelo mapeamento de triggerId (${triggerId}) para node-completed`);
      return nodeId;
    }
    
    // Se ainda não encontrou, procura nos dados do workflow pelos nós com type 'webhook_trigger'
    if (this.workflowData && Array.isArray(this.workflowData.nodes)) {
      for (const node of this.workflowData.nodes as Record<string, unknown>[]) {
        if (node.type === 'webhook_trigger' && typeof node.id === 'string') {
          // Se o nó tem um path de webhook armazenado ativo, verifica a correspondência
          if (this.activeWebhooks.has(node.id)) {
            const storedPath = this.activeWebhooks.get(node.id) || '';
            const normalizedStoredPath = this.normalizeWebhookPath(storedPath);

            if (normalizedStoredPath === normalizedPath) {
              console.log(`Correspondência encontrada pelo tipo de nó e caminho: ${node.id}`);
              return node.id;
            }
          }
        }
      }
    }
    
    console.log('Não foi possível encontrar um nó para o webhook path:', webhookPath);
    return null;
  }

  // Método para normalizar o path do webhook
  private normalizeWebhookPath(path: string): string {
    return path.startsWith('/') ? path.substring(1) : path;
  }

  // Privado: As funções abaixo são mantidas para referência futura ou possíveis implementações
  // mas não estão sendo utilizadas no momento
  /* 
  // Método para verificar se deve atualizar o status de um nó
  private shouldUpdateNodeStatus(nodeId: string, newStatus: "running" | "success" | "error" | "waiting"): boolean {
    const currentStatus = this.nodeStatuses[nodeId];
    
    // Se não tem status atual ou se o status é diferente do novo, deve atualizar
    if (!currentStatus || currentStatus !== newStatus) {
      return true;
    }
    
    // Não permitir transição de 'success' ou 'error' para outro status
    if ((currentStatus === 'success' || currentStatus === 'error') && 
        newStatus !== 'success' && newStatus !== 'error') {
      return false;
    }
    
    return false;
  }
  */
  
  // Verifica eventos gerais de nó
  /* 
  private handleNodeEvents(data: any) {
    try {
      console.log('Processando evento de nó:', data);
      
      if (data && data.nodeId) {
        const nodeId = data.nodeId;
        const status = data.status || (data.result?.success === false ? 'error' : 'success');
        
        // Atualizar o status do nó
        this.nodeStatuses[nodeId] = status;
        
        // Notificar a alteração de status
        this.callbacks.onNodeStatusChange(nodeId, status, data.result);
      } else {
        console.warn('Evento de nó sem ID válido:', data);
      }
    } catch (error) {
      console.error('Erro ao processar evento de nó:', error);
    }
  }
  */
  
  // Adicionar listener específico para workflow-completed
  private setupEventListeners(): void {
    if (!this.eventSource) {
      console.error('EventSource não está inicializado');
      return;
    }

    // Ouvir por eventos de erro específicos (além do handler global onerror)
    this.eventSource.addEventListener('error', (event) => {
      console.error('Erro no EventSource (evento específico):', event);
      
      // Não paramos o teste aqui pois já temos o handler global
      
      if ('message' in event) {
        const message = String((event as unknown as MessageEvent).data || 'Erro de conexão');
        console.error(`Mensagem de erro: ${message}`);
      }
    });
    
    // Adicionar listener específico para workflow-completed
    this.eventSource.addEventListener('workflow-completed', (event) => {
      try {
        console.log('✅ Evento workflow-completed recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        console.log('📌 Dados do workflow-completed:', data);
        
        // Notificar o componente de UI sobre a conclusão do workflow
        if (this.callbacks.onWorkflowCompleted) {
          this.callbacks.onWorkflowCompleted(data);
        }
        
        // Atualizar também o status geral do workflow
        if (this.callbacks.onWorkflowStatusChange) {
          this.callbacks.onWorkflowStatusChange('stopped', 'Workflow concluído com sucesso');
        }
        
        // Marcar como completo
        if (this.callbacks.onComplete) {
          this.callbacks.onComplete('success', data);
        }
        
        // Limpar dados de teste, já que o workflow foi concluído
        this.clearData();
      } catch (error) {
        console.error('Erro ao processar evento workflow-completed:', error);
      }
    });

    // Adicionar listener para atualização de status de nós
    this.eventSource.addEventListener('node-status', (event) => {
      try {
        console.log('Evento específico \'node-status\' recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Processar eventos de status de nós
        if (data.nodeId) {
          this.callbacks.onNodeStatusChange(data.nodeId, data.status as "running" | "success" | "error" | "waiting", data.result);
        } else if (data.id) {
          // Caso o nodeId esteja no campo 'id'
          this.callbacks.onNodeStatusChange(data.id, data.status as "running" | "success" | "error" | "waiting", data.result);
        } else if (data.triggerId) {
          // Para eventos relacionados a triggers que só têm triggerId
          const nodeId = data.triggerId;
          // Verificar se temos este triggerId mapeado para um nodeId
          const mappedNodeId = this.triggerIdToNodeIdMap.get(data.triggerId);
          
          if (mappedNodeId) {
            console.log(`Usando nodeId mapeado ${mappedNodeId} para o triggerId ${nodeId}`);
            this.callbacks.onNodeStatusChange(mappedNodeId, data.status as "running" | "success" | "error" | "waiting", data.result);
          } else {
            console.log(`Usando triggerId ${nodeId} como nodeId pois não há mapeamento`);
            this.callbacks.onNodeStatusChange(nodeId, data.status as "running" | "success" | "error" | "waiting", data.result);
          }
        }
      } catch (error) {
        console.error('Erro ao processar evento node-status:', error);
      }
    });
    
    // Adicionar listener específico para o evento webhook-registered
    this.eventSource.addEventListener('webhook-registered', (event) => {
      try {
        console.log('✅ Evento webhook-registered recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        console.log('📌 Dados do webhook-registered:', data);
        
        // Extrair informações do webhook
        // Armazenar tanto o id quanto o triggerId para mapeamento correto
        const nodeId = data.triggerId;
        const webhookId = data.id; // Guardar o ID do webhook para comparar depois
        const webhookUrl = data.fullUrl;
        
        if (nodeId) {
          console.log(`🔗 Webhook registrado para o nó ${nodeId}: ${webhookUrl}`);
          
          // Mapear o ID do webhook para o nodeId para referência futura
          if (webhookId) {
            this.triggerIdToNodeIdMap.set(webhookId, nodeId);
            console.log(`🔄 Mapeando webhookId (${webhookId}) para nodeId (${nodeId})`);
          }
          
          // Registrar o webhook ativo
          if (webhookUrl) {
            this.activeWebhooks.set(nodeId, webhookUrl);
            console.log(`ℹ️ Webhook ativo registrado: nodeId=${nodeId}, path=${webhookUrl}`);
          }
          
          // Notificar a UI sobre o webhook registrado
          this.callbacks.onTriggerWaiting('webhook', nodeId, {
            webhookUrl,
            fullUrl: data.fullUrl, // Garantir que o fullUrl seja encaminhado ao componente
            webhookId, // Incluir o ID do webhook para referência
            ...data
          });
          
          this.callbacks.onNodeStatusChange(nodeId, 'waiting');
        } else {
          console.error('❌ Evento webhook-registered sem nodeId/triggerId válido:', data);
        }
      } catch (error) {
        console.error('Erro ao processar evento webhook-registered:', error);
      }
    });
    
    // Adicionar listener específico para webhook-triggered
    this.eventSource.addEventListener('webhook-triggered', (event) => {
      try {
        console.log('Evento webhook-triggered recebido:', event);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        console.log('Dados do evento webhook-triggered:', data);
        
        // Priorizar o uso do campo 'id' para identificar o nó no workflow
        // No evento webhook-triggered, o id é o ID do webhook, não o nodeId
        let nodeId = null;
        const webhookId = data.id;
        
        // Verificar se temos este webhookId mapeado para um nodeId
        if (webhookId && this.triggerIdToNodeIdMap.has(webhookId)) {
          nodeId = this.triggerIdToNodeIdMap.get(webhookId);
          console.log(`🔍 Encontrado nodeId (${nodeId}) pelo mapeamento de webhookId (${webhookId})`);
        }
        
        // Verificar pelo triggerId também
        const triggerId = data.triggerId || data.nodeId;
        if (!nodeId && triggerId) {
          nodeId = triggerId;
          console.log(`🔍 Usando triggerId/nodeId direto: ${nodeId}`);
        }
        
        // Se ainda não tiver id, tenta outros métodos de identificação
        if (!nodeId) {
          console.log('Evento webhook-triggered sem id mapeado, tentando identificar pelo path');
          // Buscar o nodeId do nó de webhook pelo path
          const webhookPath = data.path;
          
          if (webhookPath) {
            // Tentar obter pelo mapa de webhooks ativos (método mais confiável)
            nodeId = this.getNodeIdByWebhookPath(webhookPath);
            
            // Se não encontrou, tenta pelos webhooks registrados (fallback)
            if (!nodeId && this.registeredWebhooks.length > 0) {
              const webhook = this.registeredWebhooks.find(wh => 
                wh.path === webhookPath || 
                wh.path === `/${webhookPath}` || 
                `/${wh.path}` === webhookPath
              );
              
              if (webhook) {
                nodeId = webhook.nodeId;
                console.log(`Encontrado nó de webhook pelo path ${webhookPath} nos registrados: ${nodeId}`);
              }
            }
            
            // Se encontrou o nodeId pelo path, registra-o para futuras referências
            if (nodeId) {
              console.log(`Encontrado nó de webhook pelo path ${webhookPath}: ${nodeId}`);
            }
          }
        } else {
          console.log(`Nó identificado pelo mapeamento: ${nodeId}`);
        }
        
        if (nodeId) {
          console.log(`Webhook foi acionado para o nó: ${nodeId}`);
          
          // Atualizamos o status do nó para evitar deadlocks na execução
          this.markNodeAsTriggered(nodeId);
          
          // Atualizar o status do nó para running
          this.callbacks.onNodeStatusChange(nodeId, 'running');
          
          // Fechar qualquer diálogo de espera que possa estar aberto
          if (this.callbacks.onWebhookReceived) {
            // Garantir que o evento seja identificável como webhook-triggered
            data.type = data.type || 'webhook-triggered';
            data.event = data.event || 'webhook-triggered';
            this.callbacks.onWebhookReceived(nodeId, data);
          }
        } else {
          console.error('Não foi possível identificar o nó de webhook pelo path:', data.path);
        }
      } catch (error) {
        console.error('Erro ao processar evento webhook-triggered:', error);
      }
    });
    
    // Adicionar listener para eventos de status geral do workflow
    this.eventSource.addEventListener('status', (event) => {
      try {
        console.log('Evento status do workflow recebido:', event.data);
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Verificar eventos de controle do workflow (paused, running, stopped)
        if (data.status === 'paused' || data.status === 'running' || data.status === 'stopped') {
          console.log(`Workflow ${this.executionId} está ${data.status}`);
          
          // Notificar a UI sobre a mudança de status
          this.callbacks.onWorkflowStatusChange?.(data.status, data.message);
          
          // Para o caso de stopped, podemos também chamar onComplete
          if (data.status === 'stopped') {
            this.callbacks.onComplete('stopped', data);
            this.clearData(); // Limpeza de recursos (sem fazer outra chamada de API)
          }
        }
      } catch (error) {
        console.error('Erro ao processar evento de status:', error, event.data);
      }
    });
    
    // Adicionar listener específico para schedule-registered
    this.eventSource.addEventListener('schedule-registered', (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        const nodeId = data.nodeId || data.triggerId || data.id;
        if (nodeId) {
          // Mapear o triggerId para o nodeId para referência futura
          if (data.triggerId && data.nodeId && data.triggerId !== data.nodeId) {
            this.triggerIdToNodeIdMap.set(data.triggerId, data.nodeId);
          }
          
          // Notificar a UI sobre o schedule registrado (abrir modal)
          if (this.callbacks.onScheduleRegistered) {
            this.callbacks.onScheduleRegistered(nodeId, data);
          }
          
          // Atualizar status do nó para waiting
          this.callbacks.onNodeStatusChange(nodeId, 'waiting');
        } else {
          console.error('Evento schedule-registered sem nodeId válido:', data);
        }
      } catch (error) {
        console.error('Erro ao processar evento schedule-registered:', error);
      }
    });
    
    // Adicionar listener específico para schedule-triggered
    this.eventSource.addEventListener('schedule-triggered', (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        const nodeId = data.nodeId || data.id;
        if (nodeId) {
          // Marcar o nó como triggered para evitar conflitos
          this.markNodeAsTriggered(nodeId);
          
          // Atualizar o status do nó para success pois o schedule foi executado
          this.callbacks.onNodeStatusChange(nodeId, 'success', data);
          
          // Callback específico para schedule trigger (se definido)
          if (this.callbacks.onScheduleTriggered) {
            this.callbacks.onScheduleTriggered(nodeId, data);
          }
        } else {
          console.error('Evento schedule-triggered sem nodeId válido:', data);
        }
      } catch (error) {
        console.error('Erro ao processar evento schedule-triggered:', error);
      }
    });
  }
}
